import type { Theme } from '@material-ui/core'
import { makeStyles, Popover } from '@material-ui/core'
import type { NoteActivityDetails, User } from '@paintscout/api'
import React, { useState } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import linkify from 'linkifyjs/html'
import CreateIcon from '@material-ui/icons/Create'
import moment from 'moment'
import type { QuoteFile } from 'paintscout'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import classNames from 'classnames'
import { formatName, getOwnerFromUser } from '@paintscout/util/builder'
import Highlight from '@paintscout/ui/components/Highlight'
import Typography from '@paintscout/ui/components/Typography'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import { useIsMobile } from '@paintscout/ui/hooks'
import Link from '@paintscout/ui/components/Link/Link'
import { docTypeSwitch } from 'shared/util/docTypeSwitch'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityContent from '../ActivityContent'
import ActivityTitle from '../ActivityTitle'
import ActivityActions, { ActivityAction } from '../ActivityActions'
import NoteHistoryPopover from '../../NoteHistoryPopover'
import ActivityFooter from '../ActivityFooter'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'
import { useUsers } from '../../../providers/UsersProvider'

const FIT_CONTENT = 'fit-content'
const COLOR_TRANSITION = 'color 0.2s ease-in-out'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps>((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingLeft: 0,
    paddingRight: 0,
    overflowWrap: 'break-word',
    paddingTop: 0
  },
  bubble: {
    position: 'relative',
    marginRight: 0,
    width: FIT_CONTENT,
    paddingRight: theme.spacing(3),
    '& a': {
      color: 'inherit'
    }
  },
  bubbleCorner: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderStyle: 'solid',
    borderWidth: '8px',
    borderColor: `#fff #fff ${theme.paintscout.palette.brand.Neutral.P80} ${theme.paintscout.palette.brand.Neutral.P80}`,
    ['-moz-border-radius']: '0 0 0 5px',
    borderRadius: '0 0 0 5px',
    '@media print': {
      display: 'none'
    }
  },
  note: {
    position: 'relative',
    '&:hover': {
      '& $actionsIconDiv': {
        visibility: 'visible',
        opacity: 1
      }
    }
  },
  noteContent: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: FIT_CONTENT
  },
  sentTime: {
    [theme.breakpoints.down('xs')]: {
      fontWeight: 600
    }
  },
  actionsIcon: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    '&:hover': {
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P35,
      transition: COLOR_TRANSITION
    },
    [theme.breakpoints.down('md')]: {
      height: '1.4rem',
      width: '1.4rem'
    }
  },
  fileAttachedIcon: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    height: '1rem',
    marginLeft: 0,
    alignSelf: 'center',
    '&:hover': {
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P35,
      transition: COLOR_TRANSITION
    }
  },
  actionsIconDiv: {
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.3s, visibility 0.3s',
    '&:last-child': {
      marginLeft: theme.spacing(2)
    },
    minWidth: FIT_CONTENT
  },
  moreFilesDiv: {
    '&:hover': {
      cursor: 'pointer',
      transition: COLOR_TRANSITION,
      '& *': {
        color: theme.paintscout.palette.brand.Neutral.P70
      }
    }
  },
  sentHistoryDiv: {
    cursor: 'pointer'
  },
  expandIcon: {
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  updatedDiv: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  updatedDivHover: {
    cursor: 'pointer',
    '@media (hover: hover)': {
      '&:hover': {
        transition: COLOR_TRANSITION,
        '& *': {
          color: theme.paintscout.palette.brand.Neutral.P70
        }
      }
    }
  },
  activityActions: {
    marginTop: `-${theme.spacing(0.75)}px`,
    marginBottom: 0
  },
  activityTitle: (props) =>
    props.disabled
      ? {
          marginBottom: theme.spacing(0.5)
        }
      : {},
  activityFooter: {
    width: FIT_CONTENT,
    paddingRight: 0,
    paddingBottom: 2
  },
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:visited, &:active, &:focus': {
      color: theme.palette.text.primary
    }
  }
}))

export default function NoteActivity(props: ActivityTypeComponentProps<NoteActivityDetails>) {
  const { activity, actions, onAction, disabled } = props
  const { note, files, owner, trashed, timestamp, updated, docType } = activity.details
  const { users } = useUsers()
  const userMap = Object.values(users).reduce(
    (acc, user) => {
      acc[user.user_id] = user
      return acc
    },
    {} as { [key: string]: User }
  )
  const link = activity?.docIds?.[0] ? docTypeSwitch({ eventType: 'notes', docType, id: activity.docIds[0] }) : null
  const currentOwner = userMap[owner?.id] ? getOwnerFromUser(userMap[owner?.id]) : owner
  const classes = useStyles(props)
  const mobile = useIsMobile()
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  if (trashed) {
    return (
      <ActivityContent
        classes={{
          root: classes.content,
          bubble: classes.bubble
        }}
      >
        <div className={classes.message}>
          <em>This message has been deleted</em>
        </div>
      </ActivityContent>
    )
  }
  const open = Boolean(popoverAnchorEl)
  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }
  const totalEdits = activity?.details?.versions?.length ?? 0
  const hasEdits = totalEdits > 1

  const matchingLocationWhereNoteWasAdded =
    (docType === 'deal' && location.pathname.includes('/deals/')) ||
    (docType === 'contact' && location.pathname.includes('/contact/')) ||
    (docType === 'company' && location.pathname.includes('/companies/'))

  const showActions = !disabled && actions && matchingLocationWhereNoteWasAdded
  function handleLinkClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('.no-redirect')) {
      e.preventDefault()
    }
  }

  return (
    <div className={classes.note}>
      <ActivityTitle
        className={classes.activityTitle}
        rightContent={
          showActions &&
          (mobile ? (
            <ActivityActions className={classes.activityActions}>
              <ActivityAction
                onClick={() => {
                  if (onAction) {
                    onAction('edit', activity)
                  }
                }}
                icon={CreateIcon}
              >
                Edit
              </ActivityAction>
              <ActivityAction
                onClick={() => {
                  if (onAction) {
                    onAction('delete', activity)
                  }
                }}
                icon={DeleteIcon}
              >
                Delete
              </ActivityAction>
            </ActivityActions>
          ) : (
            <div id={'actions-icon-div'} className={classes.actionsIconDiv}>
              <CreateIcon
                className={classes.actionsIcon}
                onClick={() => {
                  if (onAction) {
                    onAction('edit', activity)
                  }
                }}
              />
              <DeleteIcon
                className={classes.actionsIcon}
                onClick={() => {
                  if (onAction) {
                    onAction('delete', activity)
                  }
                }}
                style={{ marginLeft: '12px' }}
              />
            </div>
          ))
        }
      >
        <Link to={link} classes={{ root: classes.link }} onClick={handleLinkClick}>
          <Highlight>Note</Highlight> added by <Highlight>{formatName(currentOwner)}</Highlight>
        </Link>
      </ActivityTitle>
      <Link to={link} classes={{ root: classes.link }} onClick={handleLinkClick}>
        <ActivityContent
          classes={{
            root: classes.content,
            bubble: classes.bubble
          }}
        >
          <div className={classes.bubbleCorner} />
          <div className={classes.noteContent}>
            <HtmlContent content={linkify(note)} />
            {files?.length > 0 && (
              <AttachFileIcon
                className={classes.fileAttachedIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (onAction) {
                    onAction(`edit-file`, activity)
                  }
                }}
              />
            )}
          </div>
        </ActivityContent>
        {activity?.details?.legacyNote && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontStyle: 'italic', fontSize: '0.7rem', marginTop: '-5px', marginBottom: '5px' }}
          >
            This note was automatically carried over from previous contact edit form.
          </Typography>
        )}
        {onAction && (
          <>
            {[...(files?.slice(0, 3) ?? [])]?.map((file) => {
              const fullFile: QuoteFile = onAction(`file_${file}`, activity)
              if (!fullFile || !fullFile.title) {
                return null
              }
              const fileExtension = fullFile?.type?.includes('/')
                ? fullFile?.type.split('/')[1]
                : fullFile?.type ?? 'unknown'
              const title = fullFile.title
              const displayName = title.includes(fileExtension) ? title : `${title}.${fileExtension}`
              return (
                <Typography key={file} variant="body2" color="textSecondary">
                  {displayName}
                </Typography>
              )
            })}
            {files?.length > 3 && (
              <div
                className={classes.fileAttachedIcon}
                onClick={() => {
                  if (onAction) {
                    onAction(`edit-file`, activity)
                  }
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {`+ ${files?.length - 3} more files`}
                </Typography>
              </div>
            )}
          </>
        )}
        <QuoteActivitySubtitle activity={activity} showDocTypeDetails={!matchingLocationWhereNoteWasAdded} />
      </Link>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <ActivityFooter className={classes.activityFooter} timestamp={timestamp} />
        {updated && hasEdits && (
          <>
            <div
              onClick={hasEdits ? handleClick : null}
              className={classNames({
                [classes.sentHistoryDiv]: hasEdits,
                [classes.updatedDiv]: true,
                [classes.updatedDivHover]: onAction
              })}
            >
              <Typography className={classes.sentTime} variant={'subtitle2'}>
                {`- Updated ${moment(updated).fromNow()}`}
              </Typography>
              {onAction && <ExpandMoreIcon className={classes.expandIcon} />}
            </div>
            <Popover
              elevation={1}
              open={open}
              anchorEl={popoverAnchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                horizontal: -14,
                vertical: 0
              }}
            >
              <NoteHistoryPopover note={activity} totalEdits={totalEdits} onNoteClick={handleClose} />
            </Popover>
          </>
        )}
      </div>
    </div>
  )
}
